import { nextTick, reactive, ref } from 'vue';
import BaseTempWorkerEntitySetupService from '@/setup/temp-worker/BaseTempWorkerEntitySetupService';
import useCandidateService from '@/services/CandidateService';
import type OrderEntity from '@/entity/order/OrderEntity';

type Props = SetupProps<typeof TempWorkerCardSetupService.props>;

export default class TempWorkerCardSetupService extends BaseTempWorkerEntitySetupService<Props> {

	candidateService = useCandidateService();
	readonly _skills = ref<HTMLElement>();
	readonly _talents = ref<HTMLElement>();

	private _modal?: OrionModal;
	orderEntity?: OrderEntity;

	private readonly cardState = reactive({
		skillIndexToCut: undefined as Undef<number>,
		talentIndexToCut: undefined as Undef<number>,
	});

	get inModal () { return !!this._modal;}

	get canSelectCandidat () {
		return this.candidateService.inOrder()
			? this.self.can('OrderUpdate') && this.self.can('OrderCreate')
			: !this.candidateService.inCandidates();
	}

	get showActions () {
		return this.candidateService.inCandidates() || this.inModal;
	}

	get order (): OrderEntity | undefined {
		return this.orderEntity;
	}


	constructor (props: Props, _modal?: OrionModal, orderEntity?: OrderEntity) {
		super(props);
		this._modal = _modal;
		this.orderEntity = orderEntity;
	}

	protected onMounted () {
		this.setOverflowCounter();
	}


	skills () {
		const skillsToReturn: Partial<IProfessionalSkillModel>[] = this.cardState.skillIndexToCut
			? this.tempWorker?.professionalSkills?.slice(0, (this.cardState.skillIndexToCut)) ?? []
			: this.tempWorker?.professionalSkills ?? [];

		if (this.cardState.skillIndexToCut) {
			skillsToReturn.push({ label: `+${(this.tempWorker?.professionalSkills?.length ?? 0) - (this.cardState.skillIndexToCut)}` });
		}
		return skillsToReturn;
	}

	talents () {
		const talentsToReturn: Partial<ITalentModel>[] = this.cardState.talentIndexToCut
			? this.tempWorker?.talents?.slice(0, (this.cardState.talentIndexToCut)) ?? []
			: this.tempWorker?.talents ?? [];

		if (this.cardState.talentIndexToCut) {
			talentsToReturn.push({ label: `+${(this.tempWorker.talents?.length ?? 0)- (this.cardState.talentIndexToCut)}` });
		}

		return talentsToReturn;
	}

	setOverflowCounter () {
		if (!this._skills.value) return;

		const skills = this._skills.value as HTMLElement;
		const width = skills.getBoundingClientRect().width;
		const gap = +getComputedStyle(skills).gap.replace('px', '');

		nextTick(() => {
			this.getContainerIndexToCut(width, gap, 'skillIndexToCut');
			this.getContainerIndexToCut(width, gap, 'talentIndexToCut');
		});
	}

	getContainerIndexToCut (width: number, gap: number, indexString: 'skillIndexToCut' | 'talentIndexToCut') {
		let sumOfElementsWidth = 0;
		const container = indexString === 'skillIndexToCut' ? this._skills : this._talents;

		[...container.value?.children ?? []].forEach((element, index) => {
			const indexIndicatorWidth = index > 9 ? 35 : 30;
			if ((width - (sumOfElementsWidth + indexIndicatorWidth)) < 0) return;
			sumOfElementsWidth += element.getBoundingClientRect().width + gap;
			if ((width - (sumOfElementsWidth + indexIndicatorWidth)) < 0) {
				this.cardState[indexString] = index;
			}
		});
	}

	openTempWorkerAsideDetails (tab?: string, highlightElement?: string) {
		this.router.currentRoute.value.name !== 'TempWorker'
			? useCandidateService().openAsideProfile(
				{
					id: this.tempWorker.id,
					order: this.order,
					tab,
					highlightElement,
				})
			: this.tempWorker.openTempWorkerAsideDetails(highlightElement);
	}
}
